import { InputHTMLAttributes, memo, useId } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Colors, Theme } from '@app/theme';
import { Chip, TooltipText } from '@app/core';
import { RawCheckbox } from '../RawCheckbox';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
    width: '348px',
    padding: theme.marginBase * 3,

    borderRadius: theme.borderRadius.std,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: theme.colors.grayBorder,
    '&:hover': {
      backgroundColor: theme.colors.grayBackground,
    },
  },
  textRadioContainer: {
    flex: 1,
  },
  title: {
    ...theme.fonts.h5,
    textAlign: 'center',
    whiteSpace: 'pre-line',
  },
  text: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
    textAlign: 'center',
  },
  containerChecked: {
    backgroundColor: theme.colors.grayBackground,
  },
  containerDisabled: {
    opacity: 0.5,
    cursor: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  radio: {
    display: 'none',
  },
  icon: {
    color: theme.colors.green,
    position: 'absolute',
    top: theme.marginBase * 2,
    left: theme.marginBase * 2,
    fontSize: theme.iconSize.large,
  },
  topChipRight: {
    position: 'absolute',
    top: theme.marginBase * 2,
    right: theme.marginBase * 2,
  },
  topChipCenter: {
    position: 'absolute',
    top: theme.marginBase * 3,
    left: '50%',
    transform: 'translateX(-50%)',
  },
  compactContainer: {
    gap: theme.marginBase * 2,
    width: '100%',
    padding: theme.marginBase * 2,
  },
  illustration: {
    height: '100%',
    maxHeight: 100,
    maxWidth: 200,
  },
  compactIllustration: {
    width: '100%',
    height: '100%',
    maxHeight: '80px',
    maxWidth: '80px',
  },
}));

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  illustration?: string;
  title: string;
  description?: string;
  checked?: boolean;
  disabled?: boolean;
  tooltip?: string;
  centerChip?: {
    label: string;
    color: Colors | undefined;
  };
  topChip?: {
    label: string;
    color: Colors | undefined;
    position?: 'center' | 'right';
  };
  isCompact?: boolean;
  trackingName?: string;
}

export const VerticalTextAndImageSelectionBlock = memo(
  ({
    illustration,
    title,
    description,
    checked,
    disabled,
    tooltip,
    className,
    centerChip,
    topChip,
    isCompact,
    ...rest
  }: Props) => {
    const theme: Theme = useTheme();
    const classes = useStyles({ theme }) as any;
    const id = useId();
    return (
      <TooltipText title={tooltip} placement="top">
        <>
          <label
            htmlFor={id}
            className={classnames(classes.container, className, {
              [classes.containerChecked]: checked,
              [classes.containerDisabled]: disabled,
              [classes.compactContainer]: isCompact,
            })}
          >
            {topChip && (
              <Chip
                color={topChip.color}
                className={
                  topChip.position && topChip.position === 'center'
                    ? classes.topChipCenter
                    : classes.topChipRight
                }
              >
                {topChip.label}
              </Chip>
            )}
            {checked && (
              <i
                className={classnames('ri-checkbox-circle-fill', classes.icon)}
              />
            )}

            {illustration && (
              <img
                src={illustration}
                alt={title}
                className={classnames(classes.illustration, {
                  [classes.compactIllustration]: isCompact,
                })}
              />
            )}

            {centerChip && (
              <Chip color={centerChip.color}>{centerChip.label}</Chip>
            )}

            <div
              className={classnames({
                [classes.textRadioContainer]: !illustration && centerChip,
              })}
            >
              <div className={classes.title} aria-label="Label">
                {title}
              </div>
              {description && <p className={classes.text}>{description}</p>}
            </div>
          </label>

          <RawCheckbox
            id={id}
            type="radio"
            className={classes.radio}
            noLabel={true}
            checked={checked}
            disabled={disabled}
            data-tracking-name={rest.trackingName}
            {...rest}
          />
        </>
      </TooltipText>
    );
  },
);
